import { useEffect, useId } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Alert } from '@/components/atoms/Alert';
import { Button } from '@/components/atoms/Buttons';
import { Spinner } from '@/components/atoms/Spinner';
import { ModalComponent } from '@/components/molecules/ModalComponent';
import { ToastSuccess } from '@/components/molecules/ToastTemplate/ToastTemplate';
import {
    FormNumberField,
    FormTextarea,
    FormTextField,
    useServerErrorMessage,
} from '@/components/organisms/FishGroupForms';
import { type FishGroupDetail, useUpdateFishGroupMetadata } from '@/domain/fishGroups';

import {
    buildMetadataRequestBody,
    hasMetadataFieldChanges,
    type MetadataFormValues,
    toMetadataFormValues,
} from './buildMetadataRequestBody';

import styles from './actionModals.module.scss';

interface Props {
    clientId: number;
    detail: FishGroupDetail;
    isOpen: boolean;
    onClose: () => void;
}

/**
 * Correct a fish group's metadata.
 *
 * Available on every status, including closed and discarded groups — finding an
 * old typo is exactly when this is needed. That is also why the entry point
 * lives in the Metadata tab rather than in `FishGroupActionsBar`, which hides
 * itself for non-active groups.
 */
export default function EditFishGroupMetadataModal(props: Props) {
    const { clientId, detail, isOpen, onClose } = props;
    const { t } = useTranslation('fishGroups');
    const idPrefix = useId();
    const formId = `${idPrefix}-form`;
    const {
        handleSubmit,
        control,
        reset,
        formState: { isSubmitting, dirtyFields },
    } = useForm<MetadataFormValues>({ defaultValues: toMetadataFormValues(detail) });

    // Re-seed the form each time the modal opens, and whenever it is pointed at
    // a different group. Defaults come from server data, so unlike the other
    // modals in this folder they cannot be a module-level constant.
    useEffect(() => {
        if (isOpen) reset(toMetadataFormValues(detail));
    }, [isOpen, detail, reset]);

    const handleClose = () => {
        if (isSubmitting) return;
        reset(toMetadataFormValues(detail));
        onClose();
    };

    const {
        updateMetadata,
        error: serverError,
        reset: resetMutation,
    } = useUpdateFishGroupMetadata(clientId, detail.id, {
        onSuccess: () => {
            ToastSuccess(t('actions.editMetadata.successToast'), 'fish-group-metadata-success');
            resetMutation();
            onClose();
        },
    });

    const serverErrorMessage = useServerErrorMessage(serverError);

    const hasFieldChanges = hasMetadataFieldChanges(dirtyFields);

    const onSubmit = async (values: MetadataFormValues) => {
        if (!hasFieldChanges) return;
        await updateMetadata(buildMetadataRequestBody(values, dirtyFields)).catch(() => {});
    };

    const body = (
        <>
            <p className={styles.contextLine}>
                {t('actions.editMetadata.context')}{' '}
                <span className={styles.contextStrong}>{detail.name}</span>
                {detail.currentLocation && (
                    <span className={styles.contextMeta}>
                        {' · '}
                        {detail.currentLocation.cageName} ({detail.currentLocation.locationName})
                    </span>
                )}
            </p>
            <form className={styles.form} onSubmit={handleSubmit(onSubmit)} id={formId}>
                <FormTextField
                    control={control}
                    name="name"
                    label={t('actions.fields.name')}
                    rules={{ required: true }}
                />
                <FormNumberField
                    control={control}
                    name="initialPopulationSize"
                    label={t('actions.fields.initialPopulationSize')}
                    optional
                    min={1}
                    step={1}
                />
                <FormTextarea
                    control={control}
                    name="notes"
                    label={t('actions.fields.notes')}
                    optional
                />
                <FormTextarea
                    control={control}
                    name="reason"
                    label={t('actions.editMetadata.fields.reason')}
                    placeholder={t('actions.editMetadata.fields.reasonPlaceholder')}
                    rules={{ required: true }}
                />

                {serverErrorMessage && (
                    <Alert tone="danger" size="small">
                        {serverErrorMessage}
                    </Alert>
                )}
            </form>
        </>
    );

    const footer = (
        <>
            {isSubmitting && <Spinner />}
            <Button variant="secondary" onClick={handleClose} disabled={isSubmitting}>
                {t('actions.cancel')}
            </Button>
            <Button
                variant="primary"
                type="submit"
                form={formId}
                disabled={isSubmitting || !hasFieldChanges}>
                {t('actions.editMetadata.submit')}
            </Button>
        </>
    );

    return (
        <ModalComponent
            isOpen={isOpen}
            onClose={handleClose}
            title={t('actions.editMetadata.title')}
            body={body}
            footer={footer}
            backdrop="static"
        />
    );
}
